/** Firestore path: `counters/stock_<PREFIX>` with `{ prefix, next }`. */
export const STOCK_NUMBER_COUNTER_COLLECTION = "counters";

export const SHIELD_STOCK_PREFIX = "SH";
export const TRANSFERRED_SHIELD_STOCK_PREFIX = "SHV";

const STOCK_NUMBER_PATTERN = /^([A-Z]+)(\d+)$/;

export const stockNumberCounterDocId = (prefix: string): string =>
  `stock_${normalizeStockPrefix(prefix)}`;

/** Letter prefix only, e.g. `MD` or `SHV`. */
export const normalizeStockPrefix = (prefix: string): string => {
  const normalized = prefix.trim().toUpperCase();
  if (!/^[A-Z]+$/.test(normalized)) {
    throw new Error(`Invalid stock number prefix: ${prefix}`);
  }
  return normalized;
};

export const opticStockPrefix = (firstName: string, lastName: string): string =>
  normalizeStockPrefix(`${firstName.charAt(0)}${lastName.charAt(0)}`);

export const transferredOpticStockPrefix = (firstName: string, lastName: string): string =>
  normalizeStockPrefix(`${opticStockPrefix(firstName, lastName)}V`);

/** Fleet rows may carry their own two initials. Otherwise use the signed-in user. */
export const fleetOpticStockPrefix = (
  initials: string | null | undefined,
  firstName: string,
  lastName: string,
): string => {
  const cleaned = initials === "None" ? "" : (initials ?? "");
  const first = cleaned.charAt(0) || firstName.charAt(0);
  const second = cleaned.charAt(1) || lastName.charAt(0);
  return opticStockPrefix(first, second);
};

export const formatStockNumber = (prefix: string, sequence: number): string => {
  const normalized = normalizeStockPrefix(prefix);
  const padded = Math.max(1, Math.floor(sequence)).toString().padStart(5, "0");
  return `${normalized}${padded}`;
};

/** Splits `MD01173` into `{ prefix: "MD", sequence: 1173 }`. */
export const parseStockNumber = (
  value: string | null | undefined,
): { prefix: string; sequence: number } | null => {
  const trimmed = value?.trim().toUpperCase() ?? "";
  if (!trimmed) return null;
  const match = trimmed.match(STOCK_NUMBER_PATTERN);
  if (!match) return null;
  const sequence = Number(match[2]);
  if (!Number.isFinite(sequence)) return null;
  return { prefix: match[1], sequence };
};

export type StockPrefixSummary = {
  prefix: string;
  count: number;
  highestSequence: number;
  highestStockNumber: string;
  copiesAtHighest: number;
  nextSequence: number;
  nextStockNumber: string;
};

export const summarizeStockPrefixes = (
  stockNumbers: Iterable<string | null | undefined>,
): { prefixes: StockPrefixSummary[]; unparsed: string[] } => {
  const byPrefix = new Map<
    string,
    { highest: number; sample: string; count: number; atHighest: number }
  >();
  const unparsed: string[] = [];

  for (const raw of stockNumbers) {
    const trimmed = raw?.trim() ?? "";
    if (!trimmed) continue;
    const parsed = parseStockNumber(trimmed);
    if (!parsed) {
      unparsed.push(trimmed.toUpperCase());
      continue;
    }

    const current = byPrefix.get(parsed.prefix);
    if (!current) {
      byPrefix.set(parsed.prefix, {
        highest: parsed.sequence,
        sample: trimmed.toUpperCase(),
        count: 1,
        atHighest: 1,
      });
      continue;
    }

    current.count += 1;
    if (parsed.sequence > current.highest) {
      current.highest = parsed.sequence;
      current.sample = trimmed.toUpperCase();
      current.atHighest = 1;
    } else if (parsed.sequence === current.highest) {
      current.atHighest += 1;
    }
  }

  const prefixes = [...byPrefix.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([prefix, row]) => ({
      prefix,
      count: row.count,
      highestSequence: row.highest,
      highestStockNumber: row.sample,
      copiesAtHighest: row.atHighest,
      nextSequence: row.highest + 1,
      nextStockNumber: formatStockNumber(prefix, row.highest + 1),
    }));

  return { prefixes, unparsed };
};
